import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  partnerInterestsService,
  type PartnerInterest,
  type PartnerInterestStatus,
} from '@/services/partnerInterests.service'
import { PartnerInterestDetailDrawer } from './PartnerInterestDetailDrawer'
import {
  PARTNER_INTEREST_STATUSES,
  PARTNER_INTEREST_STATUS_META,
} from './partnerInterestStatus'

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-'
}

type PartnerInterestFilter = PartnerInterestStatus | 'todos' | 'followup_vencido'

export function PartnerInterestsPage() {
  const [interests, setInterests] = useState<PartnerInterest[]>([])
  const [dueInterests, setDueInterests] = useState<PartnerInterest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<PartnerInterestFilter>('todos')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [response, dueResponse] = await Promise.all([
        partnerInterestsService.list(),
        partnerInterestsService.followupsDue(),
      ])
      setInterests(response.interests)
      setDueInterests(dueResponse.interests)
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Falha ao carregar leads.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dueInterestIds = new Set(dueInterests.map((interest) => interest.id))
  const visibleInterests = interests.filter((interest) => {
    if (statusFilter === 'todos') return true
    if (statusFilter === 'followup_vencido') return dueInterestIds.has(interest.id)
    return interest.status === statusFilter
  })

  function openInterest(id: number) {
    setSelectedId(id)
    setDrawerOpen(true)
  }

  function handleDrawerOpenChange(open: boolean) {
    setDrawerOpen(open)
    if (!open) setSelectedId(null)
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads SaaS</h1>
          <p className="text-sm text-gray-500">
            Qualifique interessados e gere convites para iniciar o onboarding.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            id="status-filter"
            aria-label="Filtrar por status"
            className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm"
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value as PartnerInterestFilter)
            }
          >
            <option value="todos">Todos os status</option>
            <option value="followup_vencido">Follow-up vencido</option>
            {PARTNER_INTEREST_STATUSES.map((status) => (
              <option key={status} value={status}>
                {PARTNER_INTEREST_STATUS_META[status].label}
              </option>
            ))}
          </select>
          <Button type="button" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Atualizar
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && dueInterests.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-semibold">Follow-ups vencidos</div>
          <div>
            {dueInterests.length} lead{dueInterests.length === 1 ? ' exige' : 's exigem'} contato
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-gray-500">Carregando...</div>
      ) : visibleInterests.length === 0 ? (
        <div className="py-10 text-center text-gray-500">
          {interests.length === 0
            ? 'Nenhum lead recebido ainda.'
            : 'Nenhum lead encontrado para este status.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Lead</th>
                <th className="px-4 py-3 text-left">Empresa</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Próximo follow-up</th>
                <th className="px-4 py-3 text-left">Recebido</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {visibleInterests.map((interest) => {
                const isDue = dueInterestIds.has(interest.id)

                return (
                  <tr
                    key={interest.id}
                    className={`cursor-pointer ${
                      isDue ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => openInterest(interest.id)}
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-left font-medium text-gray-900 hover:underline"
                        onClick={(event) => {
                          event.stopPropagation()
                          openInterest(interest.id)
                        }}
                      >
                        {interest.name}
                      </button>
                      <div className="text-xs text-gray-500">{interest.email}</div>
                      {interest.phone && <div className="text-xs text-gray-400">{interest.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {interest.company_name || '-'}
                      {interest.doc && <div className="text-xs text-gray-400">{interest.doc}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          PARTNER_INTEREST_STATUS_META[interest.status].className
                        }`}
                      >
                        {PARTNER_INTEREST_STATUS_META[interest.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      <div className="flex flex-col gap-1">
                        <span>{formatDate(interest.next_follow_up_at)}</span>
                        {isDue && (
                          <span className="w-fit rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            Vencido
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(interest.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <PartnerInterestDetailDrawer
        interestId={selectedId}
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
        onUpdated={() => void load()}
      />
    </div>
  )
}
